/**
 * Phase 1 item 10 (AC3) — Consent drag-drop signature layout editor.
 *
 * Renders one page tile per PDF page and lets the coordinator drop
 * signature / initial / name / date / text fields onto each tile.
 * Coordinates are stored in PDF-point space (72 dpi, bottom-left origin).
 *
 * Wired to pdfjs-dist (2026-07-27): the actual PDF page renders as a
 * canvas underneath the drop zone so the coordinator sees WHERE on the
 * page they're placing fields. The click handler + coordinate model stay
 * unchanged (tileRef geometry drives the point-space conversion).
 *
 * Restructured (2026-09-04) into a toolbar + page-thumbnail rail +
 * right-docked inspector: field-type/signer controls moved from a tall
 * left sidebar into one top toolbar, a left rail shows small page
 * thumbnails, and the field-settings panel moved to the right alongside a
 * new "fields on this document" list. Deliberately did NOT switch to a
 * single-page-at-a-time view — every PageTile still mounts in one
 * scrollable column exactly as before, so the drag-auto-scroll effect
 * below and the "see the whole document while placing fields" workflow
 * are unaffected. The thumbnail rail is a scroll-spy/minimap only: click
 * a thumbnail to smooth-scroll to that page; the active thumbnail follows
 * scroll position via IntersectionObserver.
 *
 * Route: /consent-documents/:consentDocumentId/field-layout
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  CheckSquare,
  Clock,
  FileSignature,
  Loader2,
  MousePointer2,
  PenLine,
  Save,
  Signature,
  TextCursor,
  Trash2,
  UserRound,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
// Vite-specific asset URL — pdfjs worker MUST be served as a real URL,
// not bundled into the main chunk, or the browser refuses to spawn it.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  consent,
  consentLayout,
  type ConsentFieldPosition,
  type ConsentFieldRole,
  type ConsentFieldType,
} from '@/api/client';
import { useHasRole } from '@/components/RoleGate';
import { errorText } from '@/lib/permissionError';

// One-time worker init. At module scope so HMR reloads pick it up too.
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// The onboarders before activation, the study team after (server-checked).
const EDIT_ROLES = [
  'principal_investigator',
  'sub_investigator',
  'site_admin',
  'site_study_designer',
  'regulatory_specialist',
  'coordinator',
] as const;

// Default field dimensions (PDF points). Chosen so a signature is
// wide enough to fit a typical name and a date field is narrower.
const DEFAULT_DIMS: Record<ConsentFieldType, { width: number; height: number }> = {
  signature: { width: 220, height: 44 },
  initial: { width: 80, height: 36 },
  name: { width: 220, height: 30 },
  date: { width: 120, height: 30 },
  time: { width: 110, height: 30 },
  text: { width: 220, height: 30 },
  checkbox: { width: 22, height: 22 },
};

const FIELD_TYPE_LABEL: Record<ConsentFieldType, string> = {
  signature: 'Signature',
  initial: 'Initial',
  name: 'Name',
  date: 'Date',
  time: 'Time',
  text: 'Text',
  checkbox: 'Checkbox',
};

const FIELD_TYPE_ICON: Record<ConsentFieldType, typeof Signature> = {
  signature: Signature,
  initial: PenLine,
  name: UserRound,
  date: Calendar,
  time: Clock,
  text: TextCursor,
  checkbox: CheckSquare,
};

const ROLE_LABEL: Record<ConsentFieldRole, string> = {
  subject: 'Subject',
  parent: 'Parent / Guardian',
  lar: 'LAR',
  witness: 'Witness (retired)',
  staff: 'Staff',
  pi: 'PI',
};

const ROLE_COLOUR: Record<ConsentFieldRole, string> = {
  subject: 'border-brand-400 bg-brand-100/60 text-brand-800',
  parent: 'border-emerald-400 bg-emerald-100/60 text-emerald-800',
  lar: 'border-violet-400 bg-violet-100/60 text-violet-800',
  witness: 'border-red-400 bg-red-100/60 text-red-800',
  staff: 'border-amber-400 bg-amber-100/60 text-amber-800',
  pi: 'border-accent-400 bg-accent-100/60 text-accent-800',
};

/** Solid-fill dot colour per role — used in the fields-inspector list, where
 *  ROLE_COLOUR's border/bg/text combo (meant for a bordered box) doesn't fit. */
const ROLE_DOT: Record<ConsentFieldRole, string> = {
  subject: 'bg-brand-500',
  parent: 'bg-emerald-500',
  lar: 'bg-violet-500',
  witness: 'bg-red-500',
  staff: 'bg-amber-500',
  pi: 'bg-accent-500',
};

/**
 * Roles the editor offers. `witness` is deliberately absent — the template
 * builder rejects it, so offering it produced a layout that only failed at
 * signing time. Existing witness fields still render (in red, via
 * ROLE_LABEL/ROLE_COLOUR above) so they can be re-assigned.
 */
const SELECTABLE_ROLES: ConsentFieldRole[] = ['subject', 'parent', 'lar', 'staff', 'pi'];

// US-Letter fallback, used ONLY until pdfjs reports the real page size (see
// pageDims below) or if that read fails. Treating every PDF as Letter is what
// made fields drift on A4 documents — do not reintroduce these as the general
// case.
const PAGE_WIDTH_PT = 612;
const PAGE_HEIGHT_PT = 792;

/** Tile scale (PDF-point → CSS-px) for the main page column. */
const TILE_SCALE = 0.95;
/** Thumbnail scale for the page-navigator rail — fixed, not per-page, same
 *  as TILE_SCALE isn't varied per page either; good enough for both Letter
 *  and A4 at this size. */
const THUMB_SCALE = 0.13;

function mintFieldId(): string {
  return `f${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;
}

/**
 * Shared pdfjs page-render effect, used by both the main PageTile canvas and
 * the small thumbnail-rail canvases. Cancels the render task on unmount /
 * re-render to avoid the "Rendering cancelled" console noise pdfjs emits
 * when a new call interrupts an in-flight one.
 */
function useCanvasPageRender(
  pdfDoc: PDFDocumentProxy | null,
  pageIndex: number,
  scale: number,
  canvasRef: React.RefObject<HTMLCanvasElement>,
) {
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    const pageNumber = pageIndex + 1; // pdfjs is 1-indexed
    if (pageNumber > pdfDoc.numPages) return;
    const canvas = canvasRef.current;
    let cancelled = false;
    let renderTask: RenderTask | null = null;
    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNumber);
        if (cancelled) return;
        // Multiply by devicePixelRatio for crisp rendering on retina/hi-dpi.
        const dpr = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * dpr });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        // pdfjs v5 requires `canvas` alongside `canvasContext` in RenderParameters.
        renderTask = page.render({ canvas, canvasContext: ctx, viewport });
        await renderTask.promise;
      } catch (err) {
        // Swallow the standard "Rendering cancelled" that fires on
        // unmount races — anything else surfaces to the console for
        // triage but doesn't break the editor.
        const msg = (err as Error)?.message ?? '';
        if (!/cancel|abort/i.test(msg)) {
          console.warn(`[ConsentFieldLayoutEditor] page ${pageNumber} render failed:`, err);
        }
      }
    })();
    return () => {
      cancelled = true;
      try { renderTask?.cancel(); } catch { /* noop */ }
    };
  }, [pdfDoc, pageIndex, scale]);
}

export function ConsentFieldLayoutEditor() {
  const { consentDocumentId } = useParams<{ consentDocumentId: string }>();
  const canEdit = useHasRole(EDIT_ROLES);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const layoutQ = useQuery({
    queryKey: ['consent', 'field-layout', consentDocumentId],
    queryFn: () => consentLayout.get(consentDocumentId!),
    enabled: !!consentDocumentId,
  });

  // pdfjs pipeline — fetch the presigned S3 URL, then load the PDF.
  // Split into two queries so a stale presign (15-min TTL) is easy to
  // refresh without re-parsing the whole PDF.
  const downloadQ = useQuery({
    queryKey: ['consent', 'download-url', consentDocumentId],
    queryFn: () => consent.documentDownloadUrl(consentDocumentId!),
    enabled: !!consentDocumentId,
    // Refresh presign well before its 900s expiry so long-open editor sessions
    // don't hit a 403 when we try to load the PDF.
    staleTime: 10 * 60 * 1000,
  });
  const pdfDocQ = useQuery({
    queryKey: ['consent', 'pdf-doc', consentDocumentId, downloadQ.data?.url],
    queryFn: async () => {
      const url = downloadQ.data!.url;
      const task = pdfjsLib.getDocument({ url, disableAutoFetch: false, disableStream: false });
      return task.promise;
    },
    enabled: !!downloadQ.data?.url,
    // Loaded PDFs are large in memory — don't retry on transient errors,
    // let the operator refresh.
    retry: false,
    staleTime: Infinity,
  });

  // The page column's own scroll container — only the pages scroll; the
  // toolbar/rail/inspector stay fixed (see the layout returned below).
  const pageColumnRef = useRef<HTMLDivElement | null>(null);

  const [fields, setFields] = useState<ConsentFieldPosition[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const [activeType, setActiveType] = useState<ConsentFieldType>('signature');
  const [activeRole, setActiveRole] = useState<ConsentFieldRole>('subject');
  const [activePageIndex, setActivePageIndex] = useState(0);
  // Punch-list f24 — multiplier on top of TILE_SCALE; there was previously
  // no way to zoom in/out, only the fixed base scale.
  const [zoom, setZoom] = useState(1);
  const legacyWitnessCount = fields.filter((f) => f.role === 'witness').length;

  // Real per-page dimensions in PDF points, read from pdfjs.
  //
  // These used to be assumed to be US Letter (612x792) for every document.
  // The backend converts saved coordinates into DocuSeal fractions using the
  // TRUE page size from pdf-lib (positionedTemplateBuilder.toDocusealArea),
  // so on any non-Letter PDF — A4 (595.28x841.89) above all, which is common
  // for ICFs — the editor and the builder disagreed and fields landed in the
  // wrong place on the generated document, drifting further down the page.
  // Reading the real size here makes both ends agree.
  const [pageDims, setPageDims] = useState<Array<{ width: number; height: number }>>([]);
  useEffect(() => {
    const doc = pdfDocQ.data;
    if (!doc) return;
    let cancelled = false;
    (async () => {
      const dims: Array<{ width: number; height: number }> = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        // scale 1 → viewport units are PDF points. Rotation is already
        // applied, so a rotated/landscape page reports its visual size.
        const vp = page.getViewport({ scale: 1 });
        dims.push({ width: vp.width, height: vp.height });
      }
      if (!cancelled) setPageDims(dims);
    })().catch(() => {
      // Non-fatal — dimsForPage falls back to Letter, matching the old
      // behaviour, and the tile still renders.
    });
    return () => {
      cancelled = true;
    };
  }, [pdfDocQ.data]);

  const dimsForPage = useCallback(
    (pageIndex: number) =>
      pageDims[pageIndex] ?? { width: PAGE_WIDTH_PT, height: PAGE_HEIGHT_PT },
    [pageDims],
  );

  useEffect(() => {
    if (initialised || !layoutQ.data) return;
    setFields(layoutQ.data.layout.fields);
    setInitialised(true);
  }, [layoutQ.data, initialised]);

  // Drag auto-scroll: HTML5 drag doesn't auto-scroll long pages on most
  // browsers — the coordinator would have to release, scroll, re-drag to
  // reach page 5. Listen for our own palette drags at the document level
  // and scroll the window when the pointer nears the top/bottom edge.
  // Rate-limited via requestAnimationFrame so we don't fire scrollBy on
  // every dragover event (which fires at ~60 Hz naturally, but browsers
  // fire it much more frequently while the pointer is stationary).
  //
  // 2026-09-04: the page column now scrolls INSIDE its own container
  // (pageColumnRef) rather than at the window level (see the layout
  // restructure below — only the pages scroll, the toolbar/rail/inspector
  // stay put), so this scrolls that container directly and measures the
  // hot zone against ITS bounding rect, not the whole viewport.
  useEffect(() => {
    const HOT_ZONE_PX = 80;      // distance from edge that triggers scroll
    const MAX_SPEED_PX = 22;     // per rAF tick when pointer is at the edge
    let rafId: number | null = null;
    let currentSpeed = 0;

    function tick() {
      rafId = null;
      if (currentSpeed !== 0) {
        pageColumnRef.current?.scrollBy(0, currentSpeed);
        rafId = requestAnimationFrame(tick);
      }
    }

    function onDragOver(e: DragEvent) {
      // Only respond to our own drag payload — file drags into the tab, etc.,
      // should never trigger auto-scroll of the editor.
      if (!e.dataTransfer?.types.includes('application/x-consent-field')) {
        currentSpeed = 0;
        return;
      }
      const container = pageColumnRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const y = e.clientY;
      // Bug found live (2026-09-04): dragging from the toolbar chips (which
      // sit ABOVE the page column, not beside it, since the restructure)
      // meant the pointer started well above rect.top — `y < rect.top +
      // HOT_ZONE_PX` is true for ANY y above that threshold, including a
      // pointer over the toolbar itself, so the container scrolled to the
      // very top for the entire transit of every drag. Require y to be
      // INSIDE the container's own bounds before either hot zone can fire.
      if (y >= rect.top && y < rect.top + HOT_ZONE_PX) {
        // Closer to the edge → faster scroll (linear ramp).
        const t = 1 - (y - rect.top) / HOT_ZONE_PX;
        currentSpeed = -Math.max(4, Math.round(MAX_SPEED_PX * Math.min(1, Math.max(0, t))));
      } else if (y <= rect.bottom && y > rect.bottom - HOT_ZONE_PX) {
        const t = 1 - (rect.bottom - y) / HOT_ZONE_PX;
        currentSpeed = Math.max(4, Math.round(MAX_SPEED_PX * Math.min(1, Math.max(0, t))));
      } else {
        currentSpeed = 0;
      }
      if (currentSpeed !== 0 && rafId === null) {
        rafId = requestAnimationFrame(tick);
      }
    }

    function onDragEnd() {
      currentSpeed = 0;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragend', onDragEnd);
    document.addEventListener('drop', onDragEnd);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragend', onDragEnd);
      document.removeEventListener('drop', onDragEnd);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // Page-thumbnail-rail scroll-spy: tracks which PageTile is most in view so
  // the rail can highlight it. `root: null` (viewport) is correct even
  // though pages scroll inside pageColumnRef's own container — a page's
  // position in the VIEWPORT still changes as that container scrolls, which
  // is all IntersectionObserver needs to see.
  const pageCountForObserver = layoutQ.data?.page_count ?? pdfDocQ.data?.numPages ?? 1;
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('[data-page-index]'));
    if (!els.length) return;
    const ratios = new Map<number, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idx = Number(entry.target.getAttribute('data-page-index'));
          ratios.set(idx, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let best = -1;
        let bestRatio = -1;
        for (const [idx, r] of ratios) {
          if (r > bestRatio) {
            bestRatio = r;
            best = idx;
          }
        }
        if (bestRatio > 0) setActivePageIndex(best);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: '-10% 0px -60% 0px' },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [pageCountForObserver]);

  const scrollToPage = useCallback((pageIndex: number, block: ScrollLogicalPosition = 'start') => {
    document
      .querySelector(`[data-page-index="${pageIndex}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block });
  }, []);

  const save = useMutation({
    mutationFn: (next: ConsentFieldPosition[]) =>
      consentLayout.put(consentDocumentId!, { fields: next }),
    onSuccess: (r) => {
      toast.success(
        r.has_drag_drop_layout
          ? `Saved ${r.field_count} field${r.field_count === 1 ? '' : 's'}.`
          : 'Layout cleared.',
      );
      setDirty(false);
      qc.invalidateQueries({
        queryKey: ['consent', 'field-layout', consentDocumentId],
      });
    },
    onError: (e) => toast.error(errorText(e, 'Save failed')),
  });

  const addFieldAt = useCallback(
    (pageIndex: number, xPt: number, yPt: number) => {
      if (!canEdit) return;
      const dims = DEFAULT_DIMS[activeType];
      const page = dimsForPage(pageIndex);
      const id = mintFieldId();
      const nextField: ConsentFieldPosition = {
        field_id: id,
        field_type: activeType,
        role: activeRole,
        page_index: pageIndex,
        // Clamp origin so the field stays on the page even near the edges.
        x: Math.max(0, Math.min(xPt - dims.width / 2, page.width - dims.width)),
        y: Math.max(0, Math.min(yPt - dims.height / 2, page.height - dims.height)),
        width: dims.width,
        height: dims.height,
        required: true,
        label: `${ROLE_LABEL[activeRole]} ${FIELD_TYPE_LABEL[activeType].toLowerCase()}`,
      };
      setFields((prev) => [...prev, nextField]);
      setSelectedId(id);
      setDirty(true);
    },
    [canEdit, activeType, activeRole, dimsForPage],
  );

  const removeField = useCallback((fieldId: string) => {
    setFields((prev) => prev.filter((f) => f.field_id !== fieldId));
    setSelectedId(null);
    setDirty(true);
  }, []);

  const updateField = useCallback(
    (fieldId: string, patch: Partial<ConsentFieldPosition>) => {
      setFields((prev) =>
        prev.map((f) => (f.field_id === fieldId ? { ...f, ...patch } : f)),
      );
      setDirty(true);
    },
    [],
  );

  const clearAllFields = useCallback(() => {
    if (fields.length === 0) return;
    if (
      window.confirm(
        `Remove all ${fields.length} placed field${fields.length === 1 ? '' : 's'}? This clears the whole layout across every page.`,
      )
    ) {
      setFields([]);
      setSelectedId(null);
      setDirty(true);
    }
  }, [fields.length]);

  // Bug found live (UAT, 2026-08): "Not loading more than 5 pages" — this
  // used to fall back to a bare `?? 5` when the cached page_count column
  // was null (documents uploaded before the 2026-07-21 migration, or
  // where the upload-time pdf-lib page-count read failed/was skipped),
  // hard-capping the rendered page tiles at 5 regardless of the PDF's
  // real length. The backend's OWN write-path fallback for the identical
  // missing-data case uses 999 (fieldLayout.ts), so 5 here was always
  // just a stale placeholder. Prefer the already-loaded PDF's real page
  // count (pdfjs, via pdfDocQ) over the magic number.
  const pageCount = layoutQ.data?.page_count ?? pdfDocQ.data?.numPages ?? 1;
  const selectedField = fields.find((f) => f.field_id === selectedId) ?? null;

  if (layoutQ.isLoading) {
    return (
      <div className="p-10 text-center text-xs text-ink-muted">
        <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading layout…
      </div>
    );
  }
  if (layoutQ.isError || !layoutQ.data) {
    return (
      <div className="p-10 text-center text-xs text-danger-600">
        Failed to load consent field layout.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 lg:h-[calc(100vh-140px)]">
      <div className="flex-shrink-0">
        <LayoutToolbar
          onBack={() => navigate(-1)}
          versionLabel={layoutQ.data.version_label}
          pageCount={pageCount}
          hasDragDropLayout={layoutQ.data.has_drag_drop_layout}
          canEdit={canEdit}
          activeType={activeType}
          setActiveType={setActiveType}
          activeRole={activeRole}
          setActiveRole={setActiveRole}
          legacyWitnessCount={legacyWitnessCount}
          dirty={dirty}
          saving={save.isPending}
          onSave={() => save.mutate(fields)}
          zoom={zoom}
          setZoom={setZoom}
        />
      </div>

      {/* Three-column layout: page-thumbnail rail (navigation) | pages |
          fields inspector (settings + list). At `lg:` and above the whole
          row is height-bounded to the viewport (see the wrapper above) and
          ONLY the middle page column scrolls internally — the toolbar and
          both side columns stay put while you scroll through the document,
          per explicit feedback that the old "everything scrolls together"
          behavior (a leftover from the single-aside layout, where the whole
          page — including the toolbar — scrolled with the document) was
          disorienting. Below `lg:` everything stacks and falls back to
          ordinary document scroll, same as before. */}
      <div className="grid grid-cols-1 gap-3 lg:min-h-0 lg:flex-1 lg:grid-cols-[96px_1fr_280px] lg:items-stretch">
        <PageThumbnailRail
          pdfDoc={pdfDocQ.data ?? null}
          pageCount={pageCount}
          fields={fields}
          activePageIndex={activePageIndex}
          onJump={(pageIndex) => scrollToPage(pageIndex, 'start')}
        />

        <div ref={pageColumnRef} className="card flex flex-col items-center space-y-4 overflow-y-auto p-3 lg:min-h-0">
          {pdfDocQ.isLoading && (
            <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-ink-muted">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading PDF pages…
            </div>
          )}
          {pdfDocQ.isError && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              Couldn't load the PDF thumbnail — placing fields on blank tiles is
              still fine, coordinates are stored in PDF-point space and will map
              correctly to the real document at sign time.
            </div>
          )}
          {Array.from({ length: pageCount }).map((_, pageIndex) => (
            <PageTile
              key={pageIndex}
              pageIndex={pageIndex}
              pdfDoc={pdfDocQ.data ?? null}
              pageDim={dimsForPage(pageIndex)}
              scale={TILE_SCALE * zoom}
              fields={fields.filter((f) => f.page_index === pageIndex)}
              selectedId={selectedId}
              onAddAt={(xPt, yPt) => addFieldAt(pageIndex, xPt, yPt)}
              onSelect={setSelectedId}
              onMove={(id, xPt, yPt) => updateField(id, { x: xPt, y: yPt })}
              readOnly={!canEdit}
            />
          ))}
        </div>

        <FieldsInspector
          fields={fields}
          selectedId={selectedId}
          selectedField={selectedField}
          onSelectAndScroll={(f) => {
            setSelectedId(f.field_id);
            scrollToPage(f.page_index, 'center');
          }}
          onChange={(patch) => selectedField && updateField(selectedField.field_id, patch)}
          onDelete={() => selectedField && removeField(selectedField.field_id)}
          onClearAll={clearAllFields}
          canEdit={canEdit}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function LayoutToolbar({
  onBack,
  versionLabel,
  pageCount,
  hasDragDropLayout,
  canEdit,
  activeType,
  setActiveType,
  activeRole,
  setActiveRole,
  legacyWitnessCount,
  dirty,
  saving,
  onSave,
  zoom,
  setZoom,
}: {
  onBack: () => void;
  versionLabel: string;
  pageCount: number;
  hasDragDropLayout: boolean;
  canEdit: boolean;
  activeType: ConsentFieldType;
  setActiveType: (t: ConsentFieldType) => void;
  activeRole: ConsentFieldRole;
  setActiveRole: (r: ConsentFieldRole) => void;
  legacyWitnessCount: number;
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  zoom: number;
  setZoom: (z: number) => void;
}) {
  return (
    <div className="card space-y-2 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="mb-1 flex items-center gap-1 text-xs text-ink-muted hover:text-ink-primary"
          >
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-ink-primary">
            <FileSignature className="h-5 w-5 text-brand-500" />
            Consent field layout
          </h1>
          <p className="mt-0.5 text-xs text-ink-muted">
            Version <span className="font-mono">{versionLabel}</span> ·{' '}
            {pageCount} page{pageCount === 1 ? '' : 's'} ·{' '}
            {hasDragDropLayout
              ? 'position-driven pipeline active'
              : 'append-page pipeline active (save a layout to swap over)'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1">
            {(Object.keys(FIELD_TYPE_LABEL) as ConsentFieldType[]).map((t) => {
              const Icon = FIELD_TYPE_ICON[t];
              return (
                <button
                  key={t}
                  type="button"
                  draggable={canEdit}
                  onClick={() => setActiveType(t)}
                  onDragStart={(e) => {
                    // Payload = field type. Role comes from the current
                    // activeRole selection in the toolbar; role stays as
                    // state so users don't have to re-pick it for each drag.
                    e.dataTransfer.setData('application/x-consent-field', t);
                    e.dataTransfer.effectAllowed = 'copy';
                    setActiveType(t);
                  }}
                  className={`flex items-center gap-1 rounded border px-2 py-1.5 text-xs transition-colors ${activeType === t
                      ? 'border-accent-400 bg-accent-50/50 text-accent-700'
                      : 'border-surface-border hover:bg-surface-muted/40'
                    } ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''}`}
                  title={canEdit ? `Drag onto a page to place a ${FIELD_TYPE_LABEL[t]}` : ''}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {FIELD_TYPE_LABEL[t]}
                </button>
              );
            })}
          </div>

          <div className="h-6 w-px bg-surface-border" />

          <div className="flex flex-wrap gap-1">
            {SELECTABLE_ROLES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setActiveRole(r)}
                className={`rounded border px-2 py-1 text-[11px] ${activeRole === r
                    ? 'border-accent-400 bg-accent-50/50 text-accent-700'
                    : 'border-surface-border hover:bg-surface-muted/40'
                  }`}
              >
                {ROLE_LABEL[r]}
              </button>
            ))}
          </div>

          <div className="h-6 w-px bg-surface-border" />

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setZoom(Math.max(0.5, Math.round((zoom - 0.1) * 10) / 10))}
              disabled={zoom <= 0.5}
              className="rounded border border-surface-border px-1.5 py-1 text-xs text-ink-secondary hover:bg-surface-muted/40 disabled:opacity-40"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setZoom(1)}
              className="w-11 rounded border border-surface-border px-1 py-1 text-center text-[11px] text-ink-secondary hover:bg-surface-muted/40"
              title="Reset zoom"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              onClick={() => setZoom(Math.min(2, Math.round((zoom + 0.1) * 10) / 10))}
              disabled={zoom >= 2}
              className="rounded border border-surface-border px-1.5 py-1 text-xs text-ink-secondary hover:bg-surface-muted/40 disabled:opacity-40"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
          </div>

          {canEdit && (
            <>
              <div className="h-6 w-px bg-surface-border" />
              <button
                type="button"
                onClick={onSave}
                disabled={!dirty || saving}
                className="whitespace-nowrap rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
              >
                <Save className="mr-1 inline h-3 w-3" />
                {saving ? 'Saving…' : 'Save layout'}
              </button>
            </>
          )}
        </div>
      </div>

      {legacyWitnessCount > 0 && (
        // Saved before witness was retired. Signing would hard-fail on
        // these, so surface it here rather than at send time.
        <div className="rounded border border-red-300 bg-red-50 p-1.5 text-[10px] text-red-700">
          {legacyWitnessCount} field{legacyWitnessCount === 1 ? '' : 's'} still
          assigned to <strong>Witness</strong>, which is no longer supported.
          Select each one and change it to Parent / Guardian or LAR — signing
          will fail until you do.
        </div>
      )}

      <div className="rounded border border-surface-border bg-surface-muted/20 p-2 text-[10px] text-ink-muted">
        <MousePointer2 className="mr-1 inline h-3 w-3" />
        <strong>Drag a field type</strong> onto the page where you want it.
        Drag placed markers to reposition; click one — or a row in the
        fields list — to edit or delete it.
      </div>
    </div>
  );
}

function PageThumbnailRail({
  pdfDoc,
  pageCount,
  fields,
  activePageIndex,
  onJump,
}: {
  pdfDoc: PDFDocumentProxy | null;
  pageCount: number;
  fields: ConsentFieldPosition[];
  activePageIndex: number;
  onJump: (pageIndex: number) => void;
}) {
  return (
    <div className="hidden lg:flex lg:h-full lg:flex-col lg:items-center lg:gap-2 lg:overflow-y-auto lg:pb-2">
      {Array.from({ length: pageCount }).map((_, pageIndex) => (
        <ThumbnailCell
          key={pageIndex}
          pdfDoc={pdfDoc}
          pageIndex={pageIndex}
          isActive={activePageIndex === pageIndex}
          fieldCount={fields.filter((f) => f.page_index === pageIndex).length}
          onClick={() => onJump(pageIndex)}
        />
      ))}
    </div>
  );
}

function ThumbnailCell({
  pdfDoc,
  pageIndex,
  isActive,
  fieldCount,
  onClick,
}: {
  pdfDoc: PDFDocumentProxy | null;
  pageIndex: number;
  isActive: boolean;
  fieldCount: number;
  onClick: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useCanvasPageRender(pdfDoc, pageIndex, THUMB_SCALE, canvasRef);
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Jump to page ${pageIndex + 1}`}
      className={`relative h-[104px] w-[72px] shrink-0 overflow-hidden rounded border-2 bg-white transition-colors ${isActive ? 'border-accent-500' : 'border-surface-border hover:border-accent-300'
        }`}
    >
      {/* pdfDoc===null (still loading, or pdfDocQ errored) → the hook never
          renders, so this stays a blank cell with just the page number —
          the same "blank tile is still fine" degrade as the main column. */}
      <canvas
        ref={canvasRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full"
      />
      <span className="absolute left-1 top-0.5 text-[8px] font-semibold text-ink-muted">
        {pageIndex + 1}
      </span>
      {fieldCount > 0 && (
        <span className="absolute bottom-1 right-1 rounded-full bg-accent-600 px-1 text-[8px] font-bold text-white">
          {fieldCount}
        </span>
      )}
    </button>
  );
}

function FieldsInspector({
  fields,
  selectedId,
  selectedField,
  onSelectAndScroll,
  onChange,
  onDelete,
  onClearAll,
  canEdit,
}: {
  fields: ConsentFieldPosition[];
  selectedId: string | null;
  selectedField: ConsentFieldPosition | null;
  onSelectAndScroll: (field: ConsentFieldPosition) => void;
  onChange: (patch: Partial<ConsentFieldPosition>) => void;
  onDelete: () => void;
  onClearAll: () => void;
  canEdit: boolean;
}) {
  // Stable top-to-bottom reading order (page, then Y descending — PDF
  // point space has a bottom-left origin) rather than insertion order.
  const sorted = useMemo(
    () => [...fields].sort((a, b) => a.page_index - b.page_index || b.y - a.y),
    [fields],
  );

  return (
    <aside className="card space-y-3 overflow-y-auto p-3 lg:h-full">
      <div>
        <h2 className="mb-1 text-xs font-semibold text-ink-primary">
          Fields on this document
        </h2>
        {sorted.length === 0 ? (
          <p className="rounded border border-dashed border-surface-border bg-surface-muted/20 p-2 text-center text-[11px] text-ink-muted">
            No fields placed yet — drag a field type from the toolbar onto a
            page.
          </p>
        ) : (
          <div className="space-y-1">
            {sorted.map((f) => (
              <button
                key={f.field_id}
                type="button"
                onClick={() => onSelectAndScroll(f)}
                className={`flex w-full items-center gap-2 rounded border px-2 py-1.5 text-left text-[11px] transition-colors ${f.field_id === selectedId
                    ? 'border-accent-400 bg-accent-50/50'
                    : 'border-surface-border hover:bg-surface-muted/40'
                  }`}
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${ROLE_DOT[f.role]}`} />
                <span className="min-w-0 flex-1 truncate">
                  {FIELD_TYPE_LABEL[f.field_type]} · {ROLE_LABEL[f.role]}
                </span>
                <span className="shrink-0 text-ink-muted">p{f.page_index + 1}</span>
              </button>
            ))}
          </div>
        )}

        {canEdit && fields.length > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="mt-2 w-full rounded border border-danger-300 bg-danger-50/40 px-2 py-1 text-[11px] font-medium text-danger-700 hover:bg-danger-50"
            title="Delete every placed field on every page. Save afterwards to persist the empty layout."
          >
            <Trash2 className="mr-1 inline h-3 w-3" />
            Clear all {fields.length} field{fields.length === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {/* Selected-field settings live below the fields list so the whole
          edit surface is in one column. */}
      <div className="border-t border-surface-border pt-3">
        <h2 className="mb-2 text-xs font-semibold text-ink-primary">
          Field settings
        </h2>
        {!selectedField ? (
          <p className="rounded border border-dashed border-surface-border bg-surface-muted/20 p-2 text-center text-[11px] text-ink-muted">
            Click a placed field on the page — or a row above — to edit its
            label, size, or delete it.
          </p>
        ) : (
          <FieldSettings
            field={selectedField}
            onChange={onChange}
            onDelete={onDelete}
            readOnly={!canEdit}
          />
        )}
      </div>
    </aside>
  );
}

function PageTile({
  pageIndex,
  pdfDoc,
  pageDim,
  scale,
  fields,
  selectedId,
  onAddAt,
  onSelect,
  onMove,
  readOnly,
}: {
  pageIndex: number;
  pdfDoc: PDFDocumentProxy | null;
  /** True page size in PDF points — NOT assumed to be US Letter. */
  pageDim: { width: number; height: number };
  /** Punch-list f24 — TILE_SCALE * the toolbar's zoom multiplier. */
  scale: number;
  fields: ConsentFieldPosition[];
  selectedId: string | null;
  onAddAt: (xPt: number, yPt: number) => void;
  onSelect: (id: string) => void;
  onMove: (fieldId: string, xPt: number, yPt: number) => void;
  readOnly: boolean;
}) {
  const tileRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const dragOffset = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });

  // Sized from the real page so the drop target lines up exactly with the
  // canvas pdfjs paints below it. When these disagreed, clientToPt read
  // coordinates against the wrong page height.
  const displayWidth = pageDim.width * scale;
  const displayHeight = pageDim.height * scale;

  useCanvasPageRender(pdfDoc, pageIndex, scale, canvasRef);

  const clientToPt = useCallback((clientX: number, clientY: number) => {
    const rect = tileRef.current?.getBoundingClientRect();
    if (!rect) return { xPt: 0, yPt: 0 };
    const xPx = clientX - rect.left;
    const yPx = clientY - rect.top;
    // Bottom-left origin — invert Y.
    const xPt = xPx / scale;
    const yPt = (rect.height - yPx) / scale;
    return { xPt, yPt };
  }, [scale]);

  return (
    <div className="space-y-1" data-page-index={pageIndex}>
      <div className="text-[10px] font-medium text-ink-muted">
        Page {pageIndex + 1}
      </div>
      <div
        ref={tileRef}
        onDragOver={(e) => {
          // Only accept our own drag payload — ignore images / files / etc.
          if (readOnly) return;
          if (!e.dataTransfer.types.includes('application/x-consent-field')) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDrop={(e) => {
          if (readOnly) return;
          const raw = e.dataTransfer.getData('application/x-consent-field');
          if (!raw) return;
          e.preventDefault();
          const { xPt, yPt } = clientToPt(e.clientX, e.clientY);
          // Payload is the field type; parent's addFieldAt reads activeType +
          // activeRole from its own state so a mid-drag role change still applies.
          onAddAt(xPt, yPt);
        }}
        onMouseMove={(e) => {
          if (!dragging) return;
          const { xPt, yPt } = clientToPt(e.clientX, e.clientY);
          onMove(
            dragging,
            xPt - dragOffset.current.dx,
            yPt - dragOffset.current.dy,
          );
        }}
        onMouseUp={() => setDragging(null)}
        onMouseLeave={() => setDragging(null)}
        className="relative overflow-hidden rounded border border-dashed border-surface-border bg-white"
        style={{ width: displayWidth, height: displayHeight, maxWidth: '100%' }}
      >
        {/* PDF page rendered underneath — pointer-events:none so the click
            handler on the parent tile fires for add-field, and drag events
            on FieldMarker overlays still stopPropagation cleanly. */}
        <canvas
          ref={canvasRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full"
        />
        {fields.map((f) => (
          <FieldMarker
            key={f.field_id}
            field={f}
            pageHeightPt={pageDim.height}
            scale={scale}
            selected={f.field_id === selectedId}
            onSelect={() => onSelect(f.field_id)}
            onDragStart={(offsetXpt, offsetYpt) => {
              setDragging(f.field_id);
              dragOffset.current = { dx: offsetXpt, dy: offsetYpt };
            }}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  );
}

function FieldMarker({
  field,
  pageHeightPt,
  scale,
  selected,
  onSelect,
  onDragStart,
  readOnly,
}: {
  field: ConsentFieldPosition;
  /** True page height in PDF points — drives the bottom-left Y inversion. */
  pageHeightPt: number;
  /** Punch-list f24 — TILE_SCALE * the toolbar's zoom multiplier. */
  scale: number;
  selected: boolean;
  onSelect: () => void;
  onDragStart: (offsetXpt: number, offsetYpt: number) => void;
  readOnly: boolean;
}) {
  const displayW = field.width * scale;
  const displayH = field.height * scale;
  const displayX = field.x * scale;
  // Bottom-left origin — invert Y against the real page, not a Letter guess.
  const tileHeightPx = pageHeightPt * scale;
  const displayY = tileHeightPx - field.y * scale - displayH;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        if (readOnly) return;
        onSelect();
        // Track where inside the marker the pointer grabbed so drag doesn't jump.
        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
        const grabPtX = ((e.clientX - rect.left) / rect.width) * field.width;
        const grabPtY = field.height - ((e.clientY - rect.top) / rect.height) * field.height;
        onDragStart(grabPtX - field.width / 2, grabPtY - field.height / 2);
      }}
      className={`absolute cursor-move rounded border-2 text-[9px] font-semibold ${ROLE_COLOUR[field.role]
        } ${selected ? 'ring-2 ring-accent-500' : ''}`}
      style={{
        left: displayX,
        top: displayY,
        width: displayW,
        height: displayH,
      }}
    >
      <div className="p-0.5 leading-tight">
        {FIELD_TYPE_LABEL[field.field_type]} · {ROLE_LABEL[field.role]}
      </div>
    </div>
  );
}

function FieldSettings({
  field,
  onChange,
  onDelete,
  readOnly,
}: {
  field: ConsentFieldPosition;
  onChange: (patch: Partial<ConsentFieldPosition>) => void;
  onDelete: () => void;
  readOnly: boolean;
}) {
  const summary = useMemo(
    () =>
      `Page ${field.page_index + 1} · ${Math.round(field.x)}pt, ${Math.round(field.y)}pt · ${Math.round(field.width)}×${Math.round(field.height)}pt`,
    [field],
  );
  return (
    <div className="space-y-2 text-[11px]">
      <div className="rounded border border-surface-border bg-surface-muted/20 p-2 text-[10px] text-ink-muted">
        {summary}
      </div>
      <label className="block">
        <span className="font-medium text-ink-secondary">Label</span>
        <input
          type="text"
          value={field.label}
          onChange={(e) => onChange({ label: e.target.value })}
          disabled={readOnly}
          className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-60"
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="font-medium text-ink-secondary">Width (pt)</span>
          <input
            type="number"
            min={20}
            value={Math.round(field.width)}
            onChange={(e) => onChange({ width: Number(e.target.value) })}
            disabled={readOnly}
            className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-60"
          />
        </label>
        <label className="block">
          <span className="font-medium text-ink-secondary">Height (pt)</span>
          <input
            type="number"
            min={10}
            value={Math.round(field.height)}
            onChange={(e) => onChange({ height: Number(e.target.value) })}
            disabled={readOnly}
            className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-60"
          />
        </label>
      </div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={field.required}
          onChange={(e) => onChange({ required: e.target.checked })}
          disabled={readOnly}
        />
        <span>Required</span>
      </label>
      {!readOnly && (
        <button
          type="button"
          onClick={onDelete}
          className="mt-2 flex w-full items-center justify-center gap-1 rounded border border-danger-300 bg-danger-50 px-2 py-1 text-[11px] font-medium text-danger-700 hover:bg-danger-100"
        >
          <Trash2 className="h-3 w-3" /> Delete field
        </button>
      )}
    </div>
  );
}
